import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { InterventionLink } from "@/components/calendar/InterventionLink";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const pad = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const initial = (ev) => {
  const s = ev?.start_at ? new Date(ev.start_at) : new Date(Date.now() + 3600000);
  const e = ev?.end_at ? new Date(ev.end_at) : new Date(s.getTime() + 3600000);
  return { subject: ev?.subject || "", date: localDate(s), start: localTime(s), end: localTime(e), location: ev?.location || "", notes: ev?.preview || "", intervention_id: "", assignee_id: "" };
};

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

export const EventDialog = ({ event, onClose, onSaved, createUrl = "/calendar/events", updateUrl }) => {
  const [f, setF] = useState(initial(null));
  const [busy, setBusy] = useState(false);
  const isEdit = !!event?.id;
  const { can, me } = useAuth();
  const calName = me?.tenant?.slug ? "calendario" : "Outlook";
  const linkable = createUrl === "/calendar/events" && !updateUrl && can("interventions:plan") && (!isEdit || event?.kind === "outlook");
  const [creating, setCreating] = useState(null);
  const [createdItv, setCreatedItv] = useState(null);
  useEffect(() => { if (event) { setF(initial(event)); setCreating(null); setCreatedItv(null); } }, [event]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const invalid = !f.subject.trim() || !f.date || f.end <= f.start || (linkable && creating && (!creating.company_id || (can("interventions:assign") && !creating.lead_technician_id)));
  const ensureItv = async () => {
    if (!(linkable && creating)) return f.intervention_id;
    if (createdItv) return createdItv;
    const { data } = await api.post("/interventions", { title: f.subject.trim(), company_id: creating.company_id, type: creating.type,
      site: f.location || null, description: f.notes || null, ...(creating.lead_technician_id ? { lead_technician_id: creating.lead_technician_id } : {}) });
    setCreatedItv(data.id);
    return data.id;
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    let itvId;
    try { itvId = await ensureItv(); } catch (err) { toast.error(errorMessage(err)); setBusy(false); return; }
    const payload = {
      subject: f.subject.trim(), location: f.location || null, notes: f.notes || null, company_id: event?.company_id || null,
      start_at: new Date(`${f.date}T${f.start}`).toISOString(), end_at: new Date(`${f.date}T${f.end}`).toISOString(),
      ...(linkable && itvId ? { intervention_id: itvId, assignee_id: f.assignee_id || null } : {}),
    };
    try {
      if (isEdit) await api.patch(updateUrl || `/calendar/events/${event.id}`, payload);
      else {
        const { data } = await api.post(createUrl, payload);
        if (data?.outlook_error) toast.warning(data.outlook_error);
      }
      toast.success(payload.intervention_id ? (isEdit ? "Appuntamento collegato all'intervento: attività creata" : "Appuntamento e attività dell'intervento creati") : isEdit ? `Appuntamento aggiornato su ${calName}` : `Appuntamento creato su ${calName}`);
      onClose();
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="calendar-event-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{isEdit ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle>
            <DialogDescription>Viene salvato direttamente nel tuo {me?.tenant?.slug ? "calendario" : "calendario Outlook"}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Field label="Titolo"><Input required maxLength={255} value={f.subject} onChange={set("subject")} data-testid="calendar-event-subject" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Data"><Input type="date" required value={f.date} onChange={set("date")} data-testid="calendar-event-date" /></Field>
              <Field label="Inizio"><Input type="time" required value={f.start} onChange={set("start")} data-testid="calendar-event-start" /></Field>
              <Field label="Fine"><Input type="time" required value={f.end} onChange={set("end")} data-testid="calendar-event-end" /></Field>
            </div>
            {f.end <= f.start && <p className="text-xs text-red-600" role="alert">L'ora di fine deve essere successiva all'inizio.</p>}
            {linkable && <InterventionLink value={f.intervention_id} assignee={f.assignee_id} creating={creating} onCreating={setCreating}
              onChange={(v) => setF((x) => ({ ...x, intervention_id: v, assignee_id: "" }))} onAssignee={(v) => setF((x) => ({ ...x, assignee_id: v }))}
              onPicked={(i) => i && setF((x) => ({ ...x, subject: x.subject || `${i.number} · ${i.title}`, location: x.location || i.site || "" }))} />}
            <Field label="Luogo (facoltativo)"><Input maxLength={255} value={f.location} onChange={set("location")} data-testid="calendar-event-location" /></Field>
            <Field label="Note (facoltative)"><Textarea maxLength={2000} rows={3} value={f.notes} onChange={set("notes")} data-testid="calendar-event-notes" /></Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy || invalid} data-testid="calendar-event-save">Salva su {calName}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
